import {
  RegionalHighlightAddressMatchReason,
  RegionalHighlightListingMatchAction,
  RegionalHighlightSlotDisabledReason,
  RegionalHighlightSlotState,
  type RegionalHighlightSlot,
  type RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';

export type RegionalHighlightDraftState =
  | { kind: 'hidden' }
  | { kind: 'no-usable-slot' }
  | { kind: 'assignable'; slot: RegionalHighlightSlot }
  | { kind: 'blocked'; slot: RegionalHighlightSlot };

const isVisibleBlockedSlot = (slot: RegionalHighlightSlot): boolean =>
  slot.addressMatch?.assignable === false &&
  slot.addressMatch.reason !== RegionalHighlightAddressMatchReason.ADDRESS_INELIGIBLE &&
  slot.addressMatch.reason !== RegionalHighlightAddressMatchReason.DISTRICT_MISMATCH;

const isFutureSlot = (slot: RegionalHighlightSlot): boolean =>
  slot.status === RegionalHighlightSlotState.NOT_STARTED ||
  slot.controls.disabledReason === RegionalHighlightSlotDisabledReason.CONTRACT_NOT_STARTED ||
  slot.addressMatch?.reason === RegionalHighlightAddressMatchReason.SLOT_NOT_STARTED;

/**
 * Maps an address-preview response into the RH state offered by the new-listing wizard.
 * Creation can consume a free slot but never replaces an existing listing's booking. Future
 * contracts are omitted based on backend state and match signals, without client-side date checks.
 */
export const getRegionalHighlightDraftState = (
  response: RegionalHighlightSlotsResponse | undefined,
): RegionalHighlightDraftState => {
  if (!response?.address?.eligible) {
    return { kind: 'hidden' };
  }

  const offeredSlots = response.slots.filter((slot) => !isFutureSlot(slot));

  const assignableSlot = offeredSlots.find(
    (slot) =>
      slot.addressMatch?.assignable === true &&
      slot.addressMatch.action === RegionalHighlightListingMatchAction.ASSIGN,
  );
  if (assignableSlot) {
    return { kind: 'assignable', slot: assignableSlot };
  }

  const blockedSlot = offeredSlots.find(isVisibleBlockedSlot);
  if (blockedSlot) {
    return { kind: 'blocked', slot: blockedSlot };
  }

  return { kind: 'no-usable-slot' };
};
